import { useMemo } from "react";
import { Checks } from "@phosphor-icons/react";

/**
 * WhatsAppBubble — faithful preview of how a template renders inside
 * WhatsApp (outbound bubble, light-green background). Used in the
 * Operação "Mensagens" tab so the operator sees exactly what the
 * customer will read — including bold/italic/strike/code, emojis,
 * links and resolved variables.
 *
 * Why custom instead of a library:
 *  - No 3rd-party dep needed; the parser is ~40 lines.
 *  - We control the variable resolution to mirror what the backend
 *    orchestrator actually substitutes (`{customer_name}`, `{greeting}`,
 *    `{company_name}`, `{agent_name}`, `{agent_signature}`,
 *    `{agent_signature_short}`, `{first_name}`).
 *  - The output stays a React tree, so links remain real anchors,
 *    not innerHTML.
 *
 * Note: this is a PREVIEW only. The real send path uses the same
 * markdown but routes through the WhatsApp provider's own renderer.
 */

// ── Variable resolution ───────────────────────────────────────────────────

function getGreeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

/**
 * Replace `{key}` placeholders with values from `vars`. Unknown keys
 * are left intact (rendered in slate-400 italic so the operator can
 * spot them) — that's better than silently erasing them.
 */
function resolveVars(raw, vars) {
  if (!raw) return [];
  // Split on `{xxx}` keeping the delimiters so we can wrap unknown ones.
  const parts = raw.split(/(\{[a-z_]+\})/gi);
  return parts.map((p) => {
    const m = p.match(/^\{([a-z_]+)\}$/i);
    if (!m) return { kind: "text", value: p };
    const key = m[1].toLowerCase();
    if (vars[key] !== undefined && vars[key] !== "") {
      return { kind: "text", value: String(vars[key]) };
    }
    return { kind: "unknown", value: p };
  });
}

// ── Markdown parser ──────────────────────────────────────────────────────

// WhatsApp recognises *bold*, _italic_, ~strike~, ```code``` and
// inline `code`. We also detect bare URLs and turn them into anchors.
// The grammar is intentionally simple — WhatsApp itself is lenient,
// so we don't try to be stricter than the platform.
const URL_RE = /(https?:\/\/[^\s<>"]+)/gi;
const MD_RE = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g;

function renderInline(text, keyPrefix) {
  if (!text) return null;
  // 1) split by URL first (most distinctive boundary)
  const urlChunks = text.split(URL_RE);
  const nodes = [];
  urlChunks.forEach((chunk, i) => {
    if (URL_RE.test(chunk)) {
      // reset regex state after .test
      URL_RE.lastIndex = 0;
      nodes.push(
        <a
          key={`${keyPrefix}-u-${i}`}
          href={chunk}
          target="_blank"
          rel="noreferrer"
          className="text-[#027eb5] underline break-all"
        >
          {chunk}
        </a>
      );
      return;
    }
    URL_RE.lastIndex = 0;
    // 2) inside the non-URL chunk, split by markdown tokens
    const mdChunks = chunk.split(MD_RE);
    mdChunks.forEach((piece, j) => {
      if (!piece) return;
      if (piece.startsWith("*") && piece.endsWith("*") && piece.length > 2) {
        nodes.push(<strong key={`${keyPrefix}-b-${i}-${j}`}>{piece.slice(1, -1)}</strong>);
      } else if (piece.startsWith("_") && piece.endsWith("_") && piece.length > 2) {
        nodes.push(<em key={`${keyPrefix}-i-${i}-${j}`}>{piece.slice(1, -1)}</em>);
      } else if (piece.startsWith("~") && piece.endsWith("~") && piece.length > 2) {
        nodes.push(<s key={`${keyPrefix}-s-${i}-${j}`}>{piece.slice(1, -1)}</s>);
      } else if (piece.startsWith("`") && piece.endsWith("`") && piece.length > 2) {
        nodes.push(
          <code key={`${keyPrefix}-c-${i}-${j}`} className="font-mono text-[12px] bg-black/5 px-1 py-0.5 rounded">
            {piece.slice(1, -1)}
          </code>
        );
      } else {
        nodes.push(piece);
      }
    });
  });
  return nodes;
}

function renderResolved(parts, keyPrefix) {
  // For each resolved fragment, run the inline (markdown+url) renderer.
  // Unknown variables are shown as small italic chips so the operator
  // sees what the AI still has to fill at runtime.
  const out = [];
  parts.forEach((p, i) => {
    if (p.kind === "unknown") {
      out.push(
        <span
          key={`${keyPrefix}-uk-${i}`}
          className="text-slate-400 italic text-[12px] bg-slate-100 rounded px-1 py-0.5 mx-0.5"
          title="Variável preenchida automaticamente quando a mensagem for enviada"
        >
          {p.value}
        </span>
      );
    } else {
      const rendered = renderInline(p.value, `${keyPrefix}-r-${i}`);
      if (rendered) out.push(<span key={`${keyPrefix}-r-${i}`}>{rendered}</span>);
    }
  });
  return out;
}

/**
 * Default mock variables. The operator can override individual keys
 * by passing a `vars` prop (e.g. company_name read from settings).
 */
const DEFAULT_VARS = {
  // Canônicos (EN) — backward compat com templates antigos
  customer_name: "João Silva",
  first_name: "João",
  greeting: getGreeting(),
  company_name: "sua empresa",
  agent_name: "AgentUS",
  agent_signature: "AgentUS",
  agent_signature_short: "— AgentUS",
  // Aliases PT-BR (v0.98 hotfix) — o bubble também traduz na hora do
  // preview pra o operador ver PT-BR sendo substituído corretamente
  // mesmo se ele acabou de digitar em PT-BR (antes do save).
  nome_cliente: "João",
  nome_completo: "João Silva",
  nome_empresa: "sua empresa",
  saudacao: getGreeting(),
  atendente: "AgentUS",
  empresa: "sua empresa",
};

function formatNowHHMM(d = new Date()) {
  return d.toTimeString().slice(0, 5);
}

export default function WhatsAppBubble({
  text,
  vars,
  variant = "outbound",
  testId,
}) {
  const merged = useMemo(
    () => ({ ...DEFAULT_VARS, greeting: getGreeting(), ...(vars || {}) }),
    [vars]
  );

  const lines = useMemo(() => {
    if (!text) return [];
    return text.split("\n");
  }, [text]);

  const isOutbound = variant === "outbound";
  const bubbleCls = isOutbound
    ? "bg-[#d9fdd3] rounded-tr-[2px]"
    : "bg-white rounded-tl-[2px] border border-slate-100";

  return (
    <div
      data-testid={testId || "wa-bubble"}
      className={`relative ${isOutbound ? "ml-auto" : "mr-auto"} max-w-[85%]`}
    >
      <div className={`${bubbleCls} rounded-lg px-2.5 py-1.5 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] text-[13.5px] text-slate-800 leading-snug whitespace-pre-wrap break-words`}>
        {lines.length === 0 ? (
          <span className="text-slate-400 italic">Sem texto</span>
        ) : (
          lines.map((ln, i) => (
            <div key={i} className={i === 0 ? "" : "mt-0.5"}>
              {renderResolved(resolveVars(ln, merged), `l${i}`) || <>&nbsp;</>}
            </div>
          ))
        )}
        <div className="flex items-center justify-end gap-1 mt-1 -mb-0.5">
          <span className="text-[10px] text-slate-500/70 tabular-nums">{formatNowHHMM()}</span>
          {isOutbound && (
            <Checks size={13} weight="bold" className="text-[#53bdeb]" />
          )}
        </div>
      </div>
      {/* WhatsApp-style tail */}
      <span
        className={`absolute top-0 ${
          isOutbound ? "right-[-5px]" : "left-[-5px]"
        } w-0 h-0`}
        style={{
          borderTop: `8px solid ${isOutbound ? "#d9fdd3" : "#fff"}`,
          borderLeft: isOutbound ? "6px solid transparent" : "none",
          borderRight: isOutbound ? "none" : "6px solid transparent",
        }}
        aria-hidden
      />
    </div>
  );
}
